import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { Button } from 'components/Core/Button';
import { Switch } from 'components/Core/Switch/Switch';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { productCategoryBasicFormTranslations } from './translations';
import { IProductCategoryBasicForm, ProductCategoryBasicFormTransFields } from './types';
import styles from './productCategoryBasicForm.module.scss';

const ProductCategoryBasicForm: FC = () => {
  const {
    categoriesStore: { productCategoryData, patchProductCategoryBasic },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<ProductCategoryBasicFormTransFields>([productCategoryBasicFormTranslations.name]);

  const useFormMethods = useForm<IProductCategoryBasicForm>({
    defaultValues: {
      categoryName: productCategoryData?.categoryName || '',
      status: productCategoryData?.status || 'Active',
    },
  });

  const { handleSubmit, watch, setValue, getValues } = useFormMethods;

  const { mutate, isLoading } = useMutation(patchProductCategoryBasic, {
    mutationKey: 'patchProductCategoryBasic',
    onSuccess: () => showAlert(t('saved'), 'success'),
  });

  const onSubmitHandler = useCallback(
    (data: IProductCategoryBasicForm) => {
      mutate({ categoryName: data.categoryName, status: data.status });
    },
    [mutate]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={styles.leftSection}>
            <div className={styles.formSection}>
              <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
              <ControlledInput name='categoryName' type='text' required label={t('categoryName')} />
              <div className={styles.checkboxWrap}>
                <h6 className={styles.title}>{t('status')}</h6>
                <Switch
                  isActive={watch('status') === 'Active'}
                  onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
                />
              </div>
            </div>
            <div className={styles.bottomSection}>
              <Button title={t('save')} onClick={handleSubmit(onSubmitHandler)} isDisabled={isLoading} />
            </div>
          </section>
        </form>
      </div>
    </FormProvider>
  );
};

export default observer(ProductCategoryBasicForm);
